import type {
  FormCommentsDto,
  TFormCommentApi
} from '@/components/Shared/entities/form-comments/types/form-comments-type';
import { ApiConnector } from '@/integrations/api.connector';

import { leasingsMapper } from '../helpers';
import type {
  TIeLeasingContractDto,
  TIeLeasingCreateDto,
  TIeLeasingCreateUnverifiedDraftDto,
  TLeasingOrderDetailed,
  TLeasingOrderDetailedApi
} from '../types';

class IeLeasingApi extends ApiConnector {
  async createUnverifiedDraft(
    data: TIeLeasingCreateUnverifiedDraftDto
  ): Promise<{ requestUuid: string }> {
    const response = await this.call<unknown, { request_uuid: string }>({
      path: 'leasings/ip/createOTP',
      body: data,
      method: 'post'
    });

    return { requestUuid: response.request_uuid };
  }

  validateOtp(data: {
    uuid: string;
    otp: string;
  }): Promise<{ result: boolean; message?: string }> {
    return this.call({
      path: 'leasings/ip/validateOTP',
      body: data,
      method: 'post'
    });
  }

  retryOTP(data: {
    uuid: string;
    clientPhone: string;
    type: 'WhatsApp' | 'SMS';
  }): Promise<{ result: boolean }> {
    return this.call({
      path: 'leasings/ip/retryOTP',
      body: {
        request_uuid: data.uuid,
        clientPhone: data.clientPhone,
        type: data.type
      },
      method: 'post'
    });
  }

  async getRequestById(
    uuid: string,
    abortSignal?: AbortSignal
  ): Promise<TLeasingOrderDetailed> {
    const response = await this.call<unknown, TLeasingOrderDetailedApi>({
      path: `leasings/${uuid}`,
      abortSignal
    });

    return leasingsMapper.fromDetailedApi(response);
  }

  async create(data: TIeLeasingCreateDto): Promise<{ requestUuid: string }> {
    const { uuid, ...restDto } = data;

    const response = await this.call<unknown, { id: string }>({
      path: `leasings/${uuid}/ip`,
      method: 'patch',
      body: restDto
    });

    return { requestUuid: response.id };
  }

  acceptOffer(uuid: string) {
    return this.call({
      path: `leasings/responses/${uuid}/accept`,
      method: 'post'
    });
  }

  sendLeasingContractData(responseId: string, data: TIeLeasingContractDto) {
    return this.call({
      path: `leasings/responses/${responseId}/send-contract-details`,
      method: 'post',
      body: data
    });
  }

  createComment(body: FormCommentsDto, requestId: string) {
    return this.call({
      path: `leasings/${requestId}/createComment`,
      method: 'post',
      body
    });
  }

  updateComment(
    commentsId: string,
    body: FormCommentsDto
  ): Promise<TFormCommentApi> {
    return this.call({
      path: `leasings/${commentsId}/updateComment`,
      method: 'patch',
      body
    });
  }
}

export default new IeLeasingApi();
